import { forwardRef } from "react";
import { FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";

const base = { size: "small", fullWidth: true };

/**
 * Same adapter the forms use: emits a plain numeric string through a synthetic
 * change event so the caller never has to strip thousands separators.
 */
const NumericFormatCustom = forwardRef(
  function NumericFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
      <NumericFormat
        {...other}
        getInputRef={ref}
        onValueChange={(values) =>
          onChange({ target: { name: props.name, value: values.value } })
        }
        thousandSeparator
        valueIsNumericString
      />
    );
  },
);

/**
 * Editors keyed by `spec.type`. Each receives
 * `{ spec, value, onChange, error, disabled }`.
 *
 * Deliberately reuses what the dialogs already use — NumericFormat rather than
 * `type="number"` (iOS Safari drops invalid input silently and its keypad has
 * no minus or decimal), and the existing Selectors/* Autocomplete wrappers.
 */
export const INLINE_EDITORS = {
  text: ({ spec, value, onChange, error, disabled }) => (
    <TextField
      {...base}
      autoFocus
      disabled={disabled}
      value={value ?? ""}
      error={!!error}
      helperText={error || undefined}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ dir: "auto", ...spec.inputProps }}
    />
  ),

  number: ({ spec, value, onChange, error, disabled }) => (
    <TextField
      {...base}
      autoFocus
      disabled={disabled}
      value={value ?? ""}
      error={!!error}
      helperText={error || undefined}
      onChange={(e) => onChange(e.target.value)}
      InputProps={{ inputComponent: NumericFormatCustom, ...spec.InputProps }}
      // Latin digits inside the RTL layout read correctly only when the input
      // is explicitly LTR.
      inputProps={{ inputMode: "decimal", dir: "ltr", ...spec.inputProps }}
    />
  ),

  multiline: ({ spec, value, onChange, error, disabled }) => (
    <TextField
      {...base}
      autoFocus
      disabled={disabled}
      multiline
      minRows={spec.minRows ?? 3}
      value={value ?? ""}
      error={!!error}
      helperText={error || undefined}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ dir: "auto", ...spec.inputProps }}
    />
  ),

  /** Reuses src/components/Selectors/* — they forwardRef onto an Autocomplete. */
  select: ({ spec, value, onChange, error, disabled }) => {
    const Component = spec.component;
    if (!Component) return null;
    return (
      <Component
        fullWidth
        disabled={disabled}
        value={value ?? null}
        error={!!error}
        helperText={error || undefined}
        onChange={(e, newValue) => onChange(newValue ?? null)}
        isOptionEqualToValue={(option, current) => option?.id === current?.id}
        {...spec.componentProps}
      />
    );
  },

  /** Static `[{ value, label }]` options. */
  options: ({ spec, value, onChange, error, disabled }) => (
    <TextField
      {...base}
      select
      autoFocus
      disabled={disabled}
      value={value ?? ""}
      error={!!error}
      helperText={error || undefined}
      onChange={(e) => onChange(e.target.value)}
    >
      {(spec.options ?? []).map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  ),

  date: ({ spec, value, onChange, error, disabled }) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MobileDatePicker
        inputFormat={spec.inputFormat ?? "YYYY-MM-DD"}
        value={value ?? null}
        disabled={disabled}
        onChange={(next) =>
          onChange(
            next ? dayjs(next).format(spec.valueFormat ?? "YYYY-MM-DD") : null,
          )
        }
        renderInput={(params) => (
          <TextField
            {...base}
            {...params}
            error={!!error}
            helperText={error || undefined}
          />
        )}
      />
    </LocalizationProvider>
  ),

  boolean: ({ spec, value, onChange, disabled }) => (
    <FormControlLabel
      sx={{ m: 0 }}
      control={
        <Switch
          checked={Number(value) === 1 || value === true}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              spec.numeric === false
                ? e.target.checked
                : e.target.checked
                  ? 1
                  : 0,
            )
          }
        />
      }
      label={
        Number(value) === 1 || value === true
          ? (spec.onLabel ?? "مفعل")
          : (spec.offLabel ?? "غير مفعل")
      }
    />
  ),
};

/** Types that save on interaction instead of waiting for the ✓ button. */
export const AUTO_COMMIT_TYPES = new Set(["boolean"]);
